import React from 'react';
import { Card, Logo, Flag, Pill, EntityLink } from './ui';
import { statsForSeason, positionFamily } from '../../lib/clubHelpers';

const FAMILY_LABEL = { GK: 'Goalkeepers', DEF: 'Defenders', MID: 'Midfielders', ATT: 'Attackers' };

function nameOf(pl) {
  const p = pl?.player || pl || {};
  return p?.display_name || p?.name || p?.common_name || [p?.firstname, p?.lastname].filter(Boolean).join(' ') || '—';
}
function pid(pl) { return pl?.player_id || pl?.id || pl?.player?.id; }

export default function ClubSquad({ data }) {
  const squad = (Array.isArray(data?.squad) && data.squad.length
    ? data.squad
    : (Array.isArray(data?.overview?.players) ? data.overview.players : [])) || [];

  const stats = statsForSeason(data?.overview);
  const footing = (stats?.details || []).find((d) => d?.type?.code === 'players-footing')?.value;
  const captain = squad.find((s) => s?.captain);

  const groups = { GK: [], DEF: [], MID: [], ATT: [] };
  squad.forEach((s) => {
    const fam = positionFamily(s?.player?.position?.name || s?.player?.detailedposition?.name);
    (groups[fam] = groups[fam] || []).push(s);
  });
  const sorted = [...squad].sort((a, b) => {
    const an = a?.jersey_number, bn = b?.jersey_number;
    if (an == null) return 1;
    if (bn == null) return -1;
    return an - bn;
  });

  return (
    <>
      <Card title={`Full squad (${squad.length})`}>
        <div className="flex flex-wrap gap-2 mb-3">
          {['GK', 'DEF', 'MID', 'ATT'].map((f) =>
            groups[f].length ? <Pill key={f} className="i-bd">{FAMILY_LABEL[f]} {groups[f].length}</Pill> : null,
          )}
        </div>
        {captain && (
          <div className="i-row">
            <span className="i-mute">Captain</span>
            <span className="i-fg">{nameOf(captain)}</span>
          </div>
        )}
        {footing && (
          <div className="i-row">
            <span className="i-mute">Footing</span>
            <span className="i-fg text-xs">Right {footing.right ?? 0} · Left {footing.left ?? 0} · Both/Unknown {footing.unknown ?? 0}</span>
          </div>
        )}
      </Card>

      <Card title="Players" bodyClass="p-0">
        {squad.length > 0 && (
          <div className="overflow-x-auto">
            <table className="i-table min-w-[680px]">
              <thead>
                <tr><th>No.</th><th>Player</th><th>Position</th><th>Nationality</th><th>Captain</th></tr>
              </thead>
              <tbody>
                {sorted.map((pl, i) => {
                  const p = pl?.player || {};
                  const nat = p?.nationality || p?.country || {};
                  return (
                    <tr key={pid(pl) ?? i}>
                      <td>{pl?.jersey_number ?? ''}</td>
                      <td>
                        <span className="flex items-center gap-2">
                          <Logo src={p?.image_path} alt={nameOf(pl)} size={24} fallback={nameOf(pl)} />
                          <EntityLink to={pid(pl) ? `/player/${pid(pl)}` : null} className="i-fg">{nameOf(pl)}</EntityLink>
                        </span>
                      </td>
                      <td>{p?.detailedposition?.name || p?.position?.name || '—'}</td>
                      <td>
                        {nat?.image_path ? (
                          <span className="flex items-center gap-1.5">
                            <Flag src={nat.image_path} alt={nat?.name} size={16} />
                            <span className="i-mute text-xs">{nat?.name}</span>
                          </span>
                        ) : '—'}
                      </td>
                      <td>{pl?.captain ? <Pill className="i-pos-def">C</Pill> : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!squad.length && <p className="i-dim text-sm p-4">No squad data.</p>}
      </Card>
    </>
  );
}
